import { useEffect, useRef } from 'react';

import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { posthog } from 'posthog-js';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import {
  newSupplierDefaultValues,
  newSupplierOnChangeSchema,
  NewSupplierForm,
} from '../../forms/NewSupplier';
import { useGetCountries } from '@/api/REST/countries';
import { useCreateSupplier } from '@/api/REST/crm/suppliers';
import { useGetUserMe } from '@/api/REST/userMe';
import { useGetCurrencyCodeOptionByIsoCode } from '@/api/typesense/currencyCodes';
import { Dialog } from '@/components/ui/Dialog';
import { BaseSelectOption } from '@/forms/types';
import { useAppForm } from '@/forms/useAppForm';

interface CreateNewSupplierModalProps {
  open: boolean;
  prefilledSupplierName?: string;
  onClose: () => void;
  onCreateNewSupplierSuccess: (newSupplier: BaseSelectOption) => void;
}
export const CreateNewSupplierModal = ({
  open,
  prefilledSupplierName,
  onClose,
  onCreateNewSupplierSuccess,
}: CreateNewSupplierModalProps) => {
  const { t } = useTranslation(['forms', 'expenses']);
  const formRef = useRef<HTMLFormElement | null>(null);
  const createSupplier = useCreateSupplier();

  const { data: userMeData } = useGetUserMe();

  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetCountries();

  const { data: userCurrencySelectOption, isLoading: isUserCurrencyLoading } =
    useGetCurrencyCodeOptionByIsoCode(userMeData?.legalEntity.currencyCode);

  const userCountryOption = !isCountriesLoading
    ? countriesData?.find(
        (country) => country.id === userMeData?.legalEntity.countryCode, // on countries id is the (iso) country code
      ) || null
    : null;

  const dataLoading = isCountriesLoading || isUserCurrencyLoading;

  const form = useAppForm({
    defaultValues: {
      new_supplier: {
        ...newSupplierDefaultValues,
        supplierName:
          prefilledSupplierName || newSupplierDefaultValues.supplierName,
        currency:
          !isUserCurrencyLoading && userCurrencySelectOption
            ? userCurrencySelectOption
            : null,
        country: userCountryOption,
      },
    },
    validators: {
      onChange: z.object({
        new_supplier: newSupplierOnChangeSchema,
      }),
    },
    onSubmit: async ({ value }) => {
      const formData = value.new_supplier;
      const payload = {
        supplierName: formData.supplierName,
        supplierNo: formData.supplierNumber || undefined,
        vatNo: formData.vatNumber || undefined,
        address: formData.address || undefined,
        postalCode: formData.postalCode || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        countryCode: formData.country?.id ?? '',
        phone: formData.phoneNumber || undefined,
        email: formData.email || undefined,
        currencyCode: formData.currency?.isoCode ?? '',
      };
      await createSupplier.mutateAsync(payload, {
        onSuccess: (response) => {
          posthog.capture('supplier_created_from_expenses');

          onCreateNewSupplierSuccess({
            id: response.id.toString(),
            value: response.name,
          });
          onClose();
        },
      });
    },
    onSubmitInvalid: () => {
      /*
       * Combining form based validation with the conditional field based validation is handled by Tanstack Form in
       * a "two-stage pipeline: Stage 1 is field-level validation, and Stage 2 is form-level validation" (see: https://github.com/TanStack/form/issues/1874#issuecomment-3667665326).
       * This results in the form-level errors not displaying when Stage 1 (conditional supplier number validation) fails.
       * However, we want to display all errors on submit right way. We can force this by manually (re-)triggering the validation of the full form
       * on invalid submit regardless of whether the validation fails in Stage 1 or Stage 2, to display all errors accordingly.
       *
       * NOTE: We use .validateField() on the whole group to do the full validation and not use .validate() on the form, as that one is marked as private.
       * NOTE 2: This behavior is not really documented and the behavior could change in future versions of Tanstack Form
       */
      form.validateField('new_supplier', 'submit');
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  // Reset form when opening as it does not unmount on close
  useEffect(() => {
    if (open) {
      posthog.capture('opened_new_supplier_modal', {
        with_prefilled_name: !!prefilledSupplierName,
      });
      form.reset();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      aria-labelledby="new-supplier-dialog-title"
    >
      <form.Form ref={formRef}>
        <DialogTitle id="new-supplier-dialog-title">
          {t('expenses:newSupplierModal.Title')}
        </DialogTitle>
        <DialogContent>
          <NewSupplierForm
            fields="new_supplier"
            form={form}
            isParentLoading={dataLoading}
          />
        </DialogContent>

        <DialogActions>
          <form.SaveFormActions
            data-automation-id="create-new-supplier-save-form-actions"
            form={form}
            formRef={formRef}
            onCancel={onClose}
            onSubmit={form.handleSubmit}
            submitButtonProps={{ label: t('actions.Create') }}
          />
        </DialogActions>
      </form.Form>
    </Dialog>
  );
};
